/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from 'react';
import { Box, Grid, Typography, Paper, Button, Fab } from '@mui/material';
import { makeStyles } from '@material-ui/styles';
import Spinner from 'modules/common/spinner.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReportLineItem from './reportLineItem.component';
import ExpenseAttachmentModal from 'modules/common/ExpenseAttachmentModal.component';
import { formatMoney } from 'utilities/currencyFormatter';
import { EXPENSE_LINE_APPROVAL_STATUS, ZERO_GUID } from './expenseDetailsView.container';

const useStyles = makeStyles(theme => ({
  headerRow: { paddingBottom: '10px', borderBottom: '2px solid' },
  row: { paddingBottom: '10px', paddingTop: '10px', borderBottom: '2px solid' },
  iconOverlayImg: { position: 'absolute', zIndex: '1 !important' }
}));

const ReportLineItems = ({
  items = [],
  enumerations,
  expenseCodes,
  isReadOnly = false,
  onSaveLineItem,
  onDeleteLineItem,
  onCopyLineItem,
  expenseId,
  onBulkUpdateStatus,
  expenseDetail,
  updateParentExpenseAttachments,
  updateParentExpenseLineAttachments,
  exceptionSummaries,
  authId,
  setExceptions,
  pushToAcumatica,
  isAcumaticaRejected
}) => {
  const classes = useStyles();
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const statusOptions = useMemo(() => {
    return (enumerations?.lineItemStatuses ?? [])
      .map(s => ({
        ...s,
        title: s.id === ZERO_GUID && !s.title ? 'Draft' : s.title
      }))
      .filter(s => s.isActive);
  }, [enumerations]);

  const handleAdd = async () => {
    if (isReadOnly || !onSaveLineItem) return;
    setBusy(true);
    try {
      await onSaveLineItem(expenseId);
      //setExpandedId(created.lineItemId ?? null);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async id => {
    if (!id || !onDeleteLineItem) return;
    setBusy(true);
    try {
      await onDeleteLineItem(id);
      setExpandedId(null);
    } finally {
      setBusy(false);
    }
  };

  const handleBulkUpdateStatus = async statusId => {
    if (!statusId || !onBulkUpdateStatus) return;
    setBusy(true);
    try {
      await onBulkUpdateStatus(statusId);
      setExpandedId(null);
    } finally {
      setBusy(false);
    }
  };

  const handleCopyLineItem = async lineItemId => {
    if (!lineItemId || !onCopyLineItem) return;
    setBusy(true);
    try {
      await onCopyLineItem(lineItemId);
      setExpandedId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ marginTop: '16px' }}>
      <Paper elevation={0} square sx={{ boxShadow: 'none', backgroundColor: 'transparent', border: 'none' }}>
        <Box sx={{ padding: '16px', paddingTop: '8px' }}>
          <Box className={classes.headerRow}>
            <Grid container spacing={1} columns={{ xs: 22, sm: 22, md: 22, xl: 22 }}>
              <Grid item md={2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  Status
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  CAT
                </Typography>
              </Grid>
              <Grid item md={5}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  Description
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  Pay Option
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  Amount
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  Approved
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  Denied
                </Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  Attachment
                </Typography>
              </Grid>
              <Grid item md={1}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  {isReadOnly ? 'View' : 'Edit'}
                </Typography>
              </Grid>
              <Grid item md={1}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  {isReadOnly ? '' : 'Copy'}
                </Typography>
              </Grid>
              <Grid item md={1}>
                <Typography variant="subtitle2" sx={{ color: '#777667', fontWeight: 400 }}>
                  {isReadOnly ? '' : 'Delete'}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {busy && (
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
              <Spinner />
            </Box>
          )}
          {!busy &&
            items?.map(dto => {
              const amount = Number(dto.totalAmount ?? 0);
              const approved = Number(dto.approvedAmount ?? 0);
              const denied = Math.max(amount - approved, 0);
              const ec = dto.expenseCodeId ? expenseCodes?.find(e => e.expenseCodeId === dto.expenseCodeId) : null;
              const collapsed = (
                <Grid container spacing={1} columns={{ xs: 22, sm: 22, md: 22, xl: 22 }}>
                  <Grid item md={2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                      {statusOptions.find(s => String(s.id).toLowerCase() === String(dto.lineItemStatusId ?? '').toLowerCase())?.title ??
                        'Draft'}
                    </Typography>
                  </Grid>
                  <Grid item md={2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                      {ec?.category ?? <div>&mdash;</div>}
                    </Typography>
                  </Grid>
                  <Grid item md={5}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                      {dto.description ?? ''}
                    </Typography>
                  </Grid>
                  <Grid item md={2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                      {dto.payOption == null ? 'N/A' : dto.payOption ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item md={2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                      {formatMoney(amount)}
                    </Typography>
                  </Grid>
                  <Grid item md={2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                      {formatMoney(approved)}
                    </Typography>
                  </Grid>
                  <Grid item md={2}>
                    <Typography variant="body2" sx={{ color: '#43423a', fontWeight: 500, paddingTop: '10px' }}>
                      {formatMoney(denied)}
                    </Typography>
                  </Grid>
                  <Grid item md={2}>
                    <ExpenseAttachmentModal
                      displayText={`View Attachments (${
                        expenseDetail && expenseDetail.expenseLineAttachments
                          ? expenseDetail.expenseLineAttachments.filter(ela => ela.expenseLineItemId === dto.lineItemId).length
                          : 0
                      })`}
                      isReadOnly={isReadOnly}
                      expenseLineInfo={expenseDetail.expenseInformation}
                      expenseAttachments={expenseDetail.expenseAttachments}
                      expenseLineAttachments={
                        expenseDetail && expenseDetail.expenseLineAttachments
                          ? expenseDetail.expenseLineAttachments.filter(ela => ela.expenseLineItemId === dto.lineItemId)
                          : []
                      }
                      updateParentExpenseAttachments={updateParentExpenseAttachments}
                      updateParentExpenseLineAttachments={updateParentExpenseLineAttachments}
                      lineItemId={dto.lineItemId}
                    />
                  </Grid>
                  <Grid item md={1}>
                    <Fab className={classes.iconOverlayImg} color="primary" size="small" onClick={() => setExpandedId(dto.lineItemId)}>
                      <FontAwesomeIcon size="1x" icon={['fas', 'pen']} />
                    </Fab>
                  </Grid>
                  {!isReadOnly && (
                    <>
                      <Grid item md={1}>
                        <Fab
                          className={classes.iconOverlayImg}
                          color="secondary"
                          size="small"
                          onClick={() => handleCopyLineItem(dto.lineItemId)}
                        >
                          <FontAwesomeIcon size="1x" icon={['fas', 'copy']} />
                        </Fab>
                      </Grid>
                      <Grid item md={1}>
                        <Fab className={classes.iconOverlayImg} color="warning" size="small" onClick={() => handleDelete(dto.lineItemId)}>
                          <FontAwesomeIcon size="1x" icon={['fas', 'trash']} />
                        </Fab>
                      </Grid>
                    </>
                  )}
                </Grid>
              );
              return (
                <Box key={dto.lineItemId} className={classes.row}>
                  {expandedId !== dto.lineItemId ? (
                    collapsed
                  ) : (
                    <ReportLineItem
                      isReadOnly={isReadOnly}
                      lineItem={dto}
                      expenseCodes={expenseCodes}
                      enumerations={enumerations}
                      onSaveLineItem={async updatedDto => {
                        setBusy(true);
                        try {
                          await onSaveLineItem(updatedDto);
                          setExpandedId(null);
                        } finally {
                          setBusy(false);
                        }
                      }}
                      closeLineItem={() => setExpandedId(null)}
                      expenseDetail={expenseDetail}
                      updateParentExpenseAttachments={updateParentExpenseAttachments}
                      updateParentExpenseLineAttachments={updateParentExpenseLineAttachments}
                      exceptionSummaries={exceptionSummaries}
                      authId={authId}
                      expenseLineItemExceptions={expenseDetail.expenseLineItemExceptions}
                      setExceptions={setExceptions}
                      pushToAcumatica={pushToAcumatica}
                      isAcumaticaRejected={isAcumaticaRejected}
                    />
                  )}
                </Box>
              );
            })}

          {!isReadOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', paddingTop: '12px' }}>
              <Button
                variant="contained"
                onClick={async () => {
                  await handleBulkUpdateStatus(EXPENSE_LINE_APPROVAL_STATUS.APPROVED);
                }}
              >
                Approve All
              </Button>
              <Button
                sx={{ ml: 1 }}
                variant="contained"
                color="secondary"
                onClick={async () => {
                  await handleBulkUpdateStatus(EXPENSE_LINE_APPROVAL_STATUS.DENIED);
                }}
              >
                Deny All
              </Button>
              <Button sx={{ ml: 1 }} color="secondary" variant="contained" onClick={handleAdd}>
                Add Line
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportLineItems;
